import { AssignTastingAdmin } from '../components/AssignTastingAdmin';
import { EventSetupEditor } from '../components/EventSetupFields';
import { TestModeToggle } from '../components/TestModeToggle';
import { useState } from 'react';
import { Button, useConfirm } from '../components/ui';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tastingsApi } from '../api/tastings';
import { eventDate, PHASE_LABEL } from '../lib/format';
import { SlotAssignSelect } from '../components/RevealControls';

export function TastingSetup() {
  const confirm = useConfirm();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [count, setCount] = useState(8);

  const { data, isLoading } = useQuery({
    queryKey: ['event', id],
    queryFn: () => tastingsApi.get(id),
  });
  const { data: registrations = [] } = useQuery({
    queryKey: ['registrations', id],
    queryFn: () => tastingsApi.registrations(id),
    enabled: data?.event.canManage === true,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['event', id] });
  const addWines = useMutation({
    mutationFn: () => tastingsApi.addWines(id, count),
    onSuccess: invalidate,
  });
  const delWine = useMutation({
    mutationFn: (wineId: string) => tastingsApi.deleteWine(id, wineId),
    onSuccess: invalidate,
  });
  const goLive = useMutation({
    mutationFn: () => tastingsApi.setPhase(id, 'scoring_open'),
    onSuccess: () => navigate(`/live/${id}`),
  });
  const cancelEvent = useMutation({
    mutationFn: () => tastingsApi.deleteEvent(id),
    onSuccess: () => navigate('/tastings'),
  });

  if (isLoading || !data) return <p className="text-cream-300/60">Loading…</p>;
  const { event, wines } = data;
  if (!event.canManage) return <p>You are not assigned to run this tasting.</p>;
  const inSetup = event.phase === 'setup';
  // Which letter each attendee's bottle is under.
  const letterByRegistration = new Map(
    wines.filter((w) => w.registrationId).map((w) => [w.registrationId!, w.letter]),
  );
  const confirmed = registrations.filter((r) => r.status === 'registered');

  return (
    <div>
      <Link to="/tastings" className="text-xs text-cream-300/60 hover:text-gold-200">
        ← All tastings
      </Link>
      <div className="mt-2 mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-cream-50">{event.title}</h1>
          <p className="text-sm text-cream-300/60">
            {eventDate(event)} · {event.theme ?? 'No theme'} ·{' '}
            <span className="text-gold-300">{PHASE_LABEL[event.phase] ?? event.phase}</span>
          </p>
        </div>
        <Link to={`/live/${id}`} className="btn-ghost">
          Go to live view
        </Link>
      </div>

      <TestModeToggle event={event} wines={wines} />
      <AssignTastingAdmin eventId={id} assigned={event.tastingAdminId} />
      {event.phase !== 'final' && <EventSetupEditor event={event} />}
      <div className="card mb-6">
        <h2 className="mb-3 text-lg text-gold-200">Wines ({wines.length})</h2>
        {wines.length === 0 ? (
          <p className="text-sm text-cream-300/50">
            No wines staged yet. Add some blank slots below.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {wines.map((w) => (
              <div
                key={w.id}
                className="flex items-center gap-2 rounded-md border border-ink-800 bg-ink-950 px-3 py-2"
              >
                <span className="font-display text-lg text-cream-50">{w.letter}</span>
                {w.revealed && w.wineName && (
                  <span className="text-xs text-gold-300">{w.wineName}</span>
                )}
                {inSetup && (
                  <button
                    onClick={() => delWine.mutate(w.id)}
                    className="text-xs text-cream-300/40 hover:text-wine-300"
                    aria-label={`Remove wine ${w.letter}`}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {inSetup && (
          <div className="mt-4 flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={20}
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
              className="field w-20"
            />
            <button
              className="btn-ghost"
              onClick={() => addWines.mutate()}
              disabled={addWines.isPending}
            >
              Add {count} wine{count === 1 ? '' : 's'}
            </button>
          </div>
        )}
      </div>

      {/* Bagging: whoever bags the bottles records whose is under each letter, so
          the reveal knows the wine and who brought it. Nothing shows to the room
          until the host reveals it. */}
      {wines.length > 0 && confirmed.length > 0 && event.phase !== 'final' && (
        <div className="card mb-6">
          <h2 className="mb-1 text-lg text-gold-200">Bag the wines</h2>
          <p className="mb-4 text-sm text-cream-300/70">
            Record whose bottle is under each letter. Members see only the letter until you reveal
            it, then who brought it once you say so.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {wines.map((w) => (
              <div key={w.id} className="flex items-center gap-3">
                <span className="w-6 shrink-0 text-center font-display text-lg text-cream-50">
                  {w.letter}
                </span>
                <div className="min-w-0 flex-1">
                  <SlotAssignSelect
                    eventId={id}
                    wineId={w.id}
                    letter={w.letter}
                    registrationId={w.registrationId ?? null}
                    registrations={registrations}
                    letterByRegistration={letterByRegistration}
                    onChange={() => {
                      void invalidate();
                      void qc.invalidateQueries({ queryKey: ['live', id] });
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-cream-300/60">
            {letterByRegistration.size} of {confirmed.length} attendees’ wines bagged.
          </p>
        </div>
      )}

      <div className="card">
        <h2 className="mb-2 text-lg text-gold-200">Run the tasting</h2>
        <p className="mb-4 text-sm text-cream-300/70">
          Members score blind on their phones. You reveal each wine's identity from the live view.
        </p>
        {inSetup ? (
          <div className="flex flex-wrap gap-3">
            <button
              className="btn-primary"
              onClick={() => goLive.mutate()}
              disabled={wines.length === 0 || goLive.isPending}
            >
              Open scoring & go live
            </button>
            <Button
              variant="danger"
              loading={cancelEvent.isPending}
              onClick={async () => {
                const ok = await confirm({
                  title: 'Cancel this tasting?',
                  description: 'Every wine slot and all setup data for this event will be deleted.',
                  confirmLabel: 'Cancel tasting',
                  destructive: true,
                });
                if (ok) cancelEvent.mutate();
              }}
            >
              Cancel tasting
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-3">
            <Link to={`/live/${id}`} className="btn-primary">
              Go to live view
            </Link>
            {/* A rehearsal stays disposable after it has gone live — that's the
                whole reason to hold one. A real tasting never is: people have
                scored it by this point. */}
            {event.isTest && (
              <Button
                variant="danger"
                loading={cancelEvent.isPending}
                onClick={async () => {
                  const ok = await confirm({
                    title: 'Delete this test event?',
                    description:
                      'The rehearsal and everything in it — wines, RSVPs, scores and messages — will be permanently deleted.',
                    confirmLabel: 'Delete test event',
                    destructive: true,
                  });
                  if (ok) cancelEvent.mutate();
                }}
              >
                Delete test event
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
